import { Feature, FeatureCollection } from "geojson";
import {
  InteractionLayer,
  LayerConfig,
  MapImageId,
} from "common/components/map/layers/types";
import { Layer, Source } from "react-map-gl";
import { LinePaint } from "mapbox-gl";
import { LocationHistoryRecord } from "protos/rtc/location_history";
import React, { FC, useMemo } from "react";

interface LocationHistoryLayersProps {
  // Older records, which change rarely, and the newest records after them.
  historical: LocationHistoryRecord[];
  recent: LocationHistoryRecord[];
  entityId: string;
  beforeId: string;
  color?: string;
}
export const LocationHistoryLayers: FC<LocationHistoryLayersProps> = ({
  historical,
  recent,
  entityId,
  beforeId,
  color,
}) => {
  const historicalData = useMemo(
    () => toFeatures(historical, 0, entityId),
    [historical, entityId]
  );
  // Starts at the last historical record so the two lines join.
  const recentData = useMemo(
    () =>
      toFeatures(
        [...historical.slice(-1), ...recent],
        Math.max(historical.length - 1, 0),
        entityId
      ),
    [historical, recent, entityId]
  );

  const layerColor = color ?? "FFFFFF";
  const historicalPaint = useMemo<LinePaint>(
    () => ({
      "line-width": 2,
      "line-gradient": [
        "interpolate",
        ["linear"],
        ["line-progress"],
        -0.5,
        "#FFFFFF",
        1,
        layerColor,
      ],
    }),
    [layerColor]
  );
  const recentPaint = useMemo<LinePaint>(
    () => ({ "line-width": 2, "line-color": layerColor }),
    [layerColor]
  );

  return (
    <React.Fragment>
      <HistorySource
        data={historicalData}
        linePaint={historicalPaint}
        color={layerColor}
        beforeId={beforeId}
      />
      <HistorySource
        data={recentData}
        linePaint={recentPaint}
        color={layerColor}
        beforeId={beforeId}
      />
    </React.Fragment>
  );
};

const HistorySource: FC<{
  data: FeatureCollection;
  linePaint: LinePaint;
  color: string;
  beforeId: string;
}> = ({ data, linePaint, color, beforeId }) => (
  <Source type="geojson" lineMetrics={true} data={data}>
    <Layer beforeId={beforeId} type="line" paint={linePaint} />
    <Layer
      beforeId={beforeId}
      type="symbol"
      filter={["==", ["geometry-type"], "Point"]}
      layout={{
        "text-field": "➤",
        "text-size": 24,
        "text-rotate": ["-", ["get", "headingDegrees"], 90],
      }}
      paint={{
        "text-color": color,
      }}
    />
  </Source>
);

// The path as a line, plus a heading arrow at every 50th record counting from
// `firstIndex`, so arrows stay put as records move between buffers.
// For ground prep at 1hz average gps rate and 6mph tool speed, arrows will be
// spaced every ~300 feet. For a large field, this is 3 or more times in a
// single row. Downsampling because performance is a concern when rendering
// every point.
const toFeatures = (
  records: LocationHistoryRecord[],
  firstIndex: number,
  entityId: string
): FeatureCollection => {
  const line: Feature = {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: records.map((r) =>
        r.point ? [r.point.lng, r.point.lat] : []
      ),
    },
    properties: { entityId },
  };
  const arrows: Feature[] = records
    .filter((_, i) => (firstIndex + i) % 50 === 0)
    .map((r) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: r.point ? [r.point.lng, r.point.lat] : [],
      },
      properties: r,
    }));
  return { type: "FeatureCollection", features: [line, ...arrows] };
};

export const useLocationHistoryLayers =
  (): LayerConfig<LocationHistoryLayersProps> => {
    const interactionLayers: InteractionLayer[] = [];
    const Layers = LocationHistoryLayers;
    const imagesToLoad: MapImageId[] = [];

    return { interactionLayers, Layers, idsToLoad: imagesToLoad };
  };
